import type { Lead } from '../types';
import { formatarTelefone } from '../utils';

interface CartaoLeadProps {
  lead: Lead;
  onClick: () => void;
}

// Um lead na lista: nome, telefone, cidade, origens e eventos em uma linha.
export default function CartaoLead({ lead, onClick }: CartaoLeadProps) {
  const origens = lead.origens ?? [];
  const visiveis = origens.slice(0, 3);
  const restantes = origens.length - visiveis.length;

  return (
    <button
      type="button"
      onClick={onClick}
      className="aperta block w-full cursor-pointer rounded-[18px] bg-white p-4 text-left shadow-pcard transition-colors hover:bg-background-50"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-bold text-foreground-950">
            {lead.nome || 'Sem nome'}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground-600">
            {lead.telefone && (
              <span className="whitespace-nowrap font-medium text-foreground-700">
                {formatarTelefone(lead.telefone)}
              </span>
            )}
            {lead.cidade && (
              <span className="inline-flex min-w-0 items-center gap-1">
                <span className="flex h-4 w-4 shrink-0 items-center justify-center text-foreground-400">
                  <i className="ri-map-pin-line" aria-hidden />
                </span>
                <span className="truncate">
                  {lead.cidade}
                  {lead.uf ? `/${lead.uf}` : ''}
                </span>
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5 pt-0.5">
          {lead.tem_conta && (
            <span
              className="flex h-5 w-5 items-center justify-center text-success-600"
              title="Tem conta no app"
            >
              <i className="ri-user-star-line" aria-hidden />
            </span>
          )}
          {lead.recebe_avisos && (
            <span
              className="flex h-5 w-5 items-center justify-center text-primary-600"
              title="Recebe avisos"
            >
              <i className="ri-notification-3-line" aria-hidden />
            </span>
          )}
          <span className="flex h-5 w-5 items-center justify-center text-foreground-300">
            <i className="ri-arrow-right-s-line" aria-hidden />
          </span>
        </div>
      </div>

      {visiveis.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {visiveis.map((o) => (
            <span
              key={o}
              className="inline-flex max-w-full items-center rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-semibold text-secondary-700"
            >
              <span className="truncate">{o}</span>
            </span>
          ))}
          {restantes > 0 && (
            <span className="inline-flex items-center rounded-full bg-background-200 px-2.5 py-0.5 text-xs font-semibold text-foreground-600">
              +{restantes}
            </span>
          )}
        </div>
      )}

      {lead.aceita_whatsapp === true && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          <span className="inline-flex items-center gap-1 rounded-full bg-success-100 px-2.5 py-0.5 text-xs font-semibold text-success-700">
            <i className="ri-whatsapp-line" aria-hidden /> WhatsApp
          </span>
        </div>
      )}
      {lead.aceita_whatsapp === false && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-semibold text-secondary-600">
            <i className="ri-close-line" aria-hidden /> Não quer
          </span>
        </div>
      )}

      {lead.eventos.length > 0 && (
        <p className="mt-2 line-clamp-1 text-xs text-foreground-500">
          {lead.eventos.join(' · ')}
        </p>
      )}
    </button>
  );
}